// Client entity resolution — shared by Quick Add (paste flow) and BookingDetail.
//
// A "client" is a reusable contact record in the `clients` collection. Bookings
// link to one via booking.clientId (nullable). Matching is by normalized email
// (primary) or phone (fallback). Per Dan's call, linking is always
// confirm-before-link in the UI — these helpers only find/create, they never
// auto-link silently.

import {
  collection, query, where, getDocs, addDoc, doc, getDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

export function normalizeEmail(email) {
  if (!email) return null;
  const e = String(email).trim().toLowerCase();
  return e || null;
}

// Digits-only phone key for matching. Returns null for anything too short to
// be a real number (avoids matching on stray "0" / extension fragments).
export function normalizePhone(phone) {
  if (!phone) return null;
  const d = String(phone).replace(/\D/g, "");
  return d.length >= 7 ? d : null;
}

// Doc shape for a new client. Match keys (emailLower/phoneDigits) are stored
// alongside the display fields so lookups are single-field (auto-indexed).
export function defaultClient({ name, company, email, phone } = {}) {
  return {
    name: name || null,
    company: company || null,
    email: email || null,
    phone: phone || null,
    emailLower: normalizeEmail(email),
    phoneDigits: normalizePhone(phone),
    notes: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

// Find existing clients matching by email (primary) or phone (fallback).
// Returns [{id, ...data}] — empty array means no match. De-duped across both
// lookups by doc id.
export async function findMatchingClients({ email, phone }) {
  const emailLower = normalizeEmail(email);
  const phoneDigits = normalizePhone(phone);
  const found = new Map();
  if (emailLower) {
    const snap = await getDocs(query(collection(db, "clients"), where("emailLower", "==", emailLower)));
    snap.forEach((d) => found.set(d.id, { id: d.id, ...d.data() }));
  }
  if (phoneDigits) {
    const snap = await getDocs(query(collection(db, "clients"), where("phoneDigits", "==", phoneDigits)));
    snap.forEach((d) => found.set(d.id, { id: d.id, ...d.data() }));
  }
  return Array.from(found.values());
}

// Create a new client doc, return its id.
export async function createClient(info) {
  const ref = await addDoc(collection(db, "clients"), defaultClient(info));
  return ref.id;
}

// Fetch a single client doc, or null.
export async function getClient(clientId) {
  if (!clientId) return null;
  const snap = await getDoc(doc(db, "clients", clientId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// All bookings linked to a client. Sorted client-side (newest first) to avoid
// a composite index on (clientId, confirmedDate).
export async function getClientBookings(clientId) {
  if (!clientId) return [];
  const snap = await getDocs(query(collection(db, "bookings"), where("clientId", "==", clientId)));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const millis = (b) => {
    const v = b.confirmedDate || b.inquiryEventDate || b.createdAt;
    if (!v) return 0;
    if (typeof v.toMillis === "function") return v.toMillis();
    const d = new Date(v);
    return isNaN(d) ? 0 : d.getTime();
  };
  return rows.sort((a, b) => millis(b) - millis(a));
}
